import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Mail, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/i18n";

/** ForgotPassword — sends a reset request; the link is delivered by email only. */
export default function ForgotPassword() {
  const { tf } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", {
        email: email.trim().toLowerCase(),
      });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || tf("Erreur"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0A0C12] flex items-center justify-center px-5 py-10" data-testid="forgot-page">
      <div className="w-full max-w-sm">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-[#93A5C0] hover:text-white transition-colors mb-6">
          <ArrowLeft className="h-3.5 w-3.5" />
          {tf("Retour")}
        </Link>

        <div className="flex items-center gap-2 mb-6">
          <img src="/athlynx-logo-white.svg" alt="Athlynx" className="h-7 w-7" />
          <span className="text-base font-semibold tracking-tight text-white">Athlynx</span>
        </div>

        <header className="mb-7 space-y-1">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#F5F7FA]">
            {tf("Mot de passe oublié ?")}
          </h1>
          <p className="text-sm text-[#9AA9BF]">
            {tf("Entrez votre email pour recevoir un lien de réinitialisation.")}
          </p>
        </header>

        {!sent ? (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs text-[#C4D0E2]">{tf("Email")}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7C8CA5]" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={tf("vous@exemple.com")}
                  className="pl-9 h-11 bg-[#111B28] border-[#354458] text-[#F8FAFC] placeholder:text-[#94A3B8] focus-visible:border-[#5B739B]"
                  data-testid="forgot-email"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 gap-2 active:scale-[0.98] transition-all duration-150 bg-[#2458D6] text-white hover:bg-[#1E4BBE] disabled:opacity-60"
              data-testid="forgot-submit"
            >
              {busy ? tf("Envoi…") : (<>{tf("Envoyer le lien")} <ArrowRight className="h-4 w-4" /></>)}
            </Button>
          </form>
        ) : (
          <div className="space-y-4" data-testid="forgot-success">
            <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] p-4 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-medium text-emerald-400">{tf("Demande envoyée")}</div>
                <p className="text-emerald-400/80 text-xs mt-1 leading-relaxed">
                  {tf("Si un compte existe avec cet email, un lien de réinitialisation sera envoyé.")}
                </p>
              </div>
            </div>

            <Button asChild variant="outline" className="w-full h-11 bg-transparent border-white/[0.16] text-white hover:bg-white/[0.06] hover:text-white" data-testid="forgot-back-login">
              <Link to="/login">{tf("Retour à la connexion")}</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
